import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../../components/layout";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import {
  getFreightActiveUserProfiling,
  storeFreightActiveUserProfiling,
  getVehicleCapacities
} from "../../../utils/api";
import { showError, showSuccess } from "../../../utils/toast";
import FreightProfilingLocationCombo, {
  hydrateProfilingSelectionLabels,
  profilingSideToSelections,
  selectionsToProfilingRows
} from "../../../components/admin/FreightProfilingLocationCombo";
import "./adminFreight.css";

function formatTonnes(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return String(n ?? "");
  return String(Math.round(x));
}

const FreightProfilingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = [
    { label: roleLabel, link: "/dashboard" },
    { label: "Freight", link: "/freight" },
    { label: `Profiling ${id}` }
  ];

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fu, setFu] = useState(null);
  const [caps, setCaps] = useState([]);
  const [loadingSelections, setLoadingSelections] = useState([]);
  const [unloadingSelections, setUnloadingSelections] = useState([]);
  const [vehicleIds, setVehicleIds] = useState([]);
  const [loadingLocationSummary, setLoadingLocationSummary] = useState(null);
  const [unloadingLocationSummary, setUnloadingLocationSummary] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [prof, vc] = await Promise.all([
          getFreightActiveUserProfiling(id),
          getVehicleCapacities()
        ]);
        const d = prof?.data ?? {};
        setFu(d.freight_user);
        const capList =
          (vc?.data?.list ?? vc?.data?.vehicle_capacities ?? []).length > 0
            ? vc?.data?.list ?? vc?.data?.vehicle_capacities
            : d.vehicle_capacities ?? [];
        setCaps(Array.isArray(capList) ? capList : []);

        const L = d.loading || {};
        const U = d.unloading || {};
        setLoadingLocationSummary(L.location_summary ?? null);
        setUnloadingLocationSummary(U.location_summary ?? null);

        const Lraw = profilingSideToSelections(L);
        const Uraw = profilingSideToSelections(U);
        const [Lh, Uh] = await Promise.all([
          hydrateProfilingSelectionLabels(Lraw),
          hydrateProfilingSelectionLabels(Uraw)
        ]);
        setLoadingSelections(Lh);
        setUnloadingSelections(Uh);

        const rawVc =
          d.loading?.vehicle_capacity_ids ||
          d.unloading?.vehicle_capacity_ids ||
          d.vehicle_capacity_ids ||
          [];
        setVehicleIds(rawVc.map((x) => Number(x)));
      } catch (e) {
        showError(e.message || "Failed to load profiling");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const loadingRows = selectionsToProfilingRows(loadingSelections);
      const unloadingRows = selectionsToProfilingRows(unloadingSelections);
      const body = {
        vehicle_capacity_ids: vehicleIds,
        loading: { rows: loadingRows },
        unloading: { rows: unloadingRows }
      };
      await storeFreightActiveUserProfiling(id, body);
      showSuccess("Profiling saved");
      navigate("/freight/active_users");
    } catch (e) {
      showError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const title = fu?.name || fu?.code || id;

  const fmtCounts = (s) => {
    if (!s) return "—";
    const bits = [];
    if (s.row_count != null) bits.push(`${s.row_count} rule row(s)`);
    else if (s.display_count != null) bits.push(`${s.display_count} row(s)`);
    if (s.triple_count != null) bits.push(`${s.triple_count} triple(s)`);
    if (s.city_count != null || s.state_count != null || s.zone_count != null) {
      bits.push(
        [
          s.city_count != null ? `${s.city_count} cities` : null,
          s.state_count != null ? `${s.state_count} states` : null,
          s.zone_count != null ? `${s.zone_count} zones` : null
        ]
          .filter(Boolean)
          .join(", ")
      );
    }
    return bits.length ? bits.join(" · ") : "—";
  };

  const alignNote =
    loadingLocationSummary?.align_note || unloadingLocationSummary?.align_note;

  const loadingCountLine =
    loadingLocationSummary?.display_count != null ||
    unloadingLocationSummary?.display_count != null ||
    loadingLocationSummary?.row_count != null ||
    unloadingLocationSummary?.row_count != null ? (
      <div className="freight-profiling-hint mt-0 space-y-1 rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2.5 text-sm text-slate-600">
        <p className="m-0 leading-relaxed">
          Server summary — loading: <strong className="text-slate-800">{fmtCounts(loadingLocationSummary)}</strong>
          {" · "}
          unloading: <strong className="text-slate-800">{fmtCounts(unloadingLocationSummary)}</strong>
        </p>
        {alignNote ? (
          <p className="m-0 text-xs text-amber-800">
            <strong>Note:</strong> {alignNote}
          </p>
        ) : null}
      </div>
    ) : null;

  if (loading) {
    return (
      <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
        <div className="freight-admin-shell freight-admin-shell--narrow">
          <div className="card offers-table rounded-xl border border-slate-100 shadow-sm">
            <p className="freight-admin-muted mb-0 text-sm">Loading profiling…</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="freight-admin-shell freight-admin-shell--narrow freight-admin-shell--pb">
        <div className="card offers-table rounded-xl border border-slate-100 shadow-sm">
          <div className="freight-form-page-header flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1">
              <h3 className="freight-user-form-heading">Profiling — {title}</h3>
              <p className="freight-form-lead text-pretty">
                Choose <strong>vehicle capacity bands (tonnes)</strong> once — they apply to every location rule on
                save. Add <strong>loading</strong> and <strong>unloading</strong> rules as rows: each row is{" "}
                <code className="rounded bg-slate-100 px-1 text-xs">zone_id</code>,{" "}
                <code className="rounded bg-slate-100 px-1 text-xs">state_id</code>, and{" "}
                <code className="rounded bg-slate-100 px-1 text-xs">city_ids</code> (use <strong>0</strong> or{" "}
                <strong>[0]</strong> where the product allows “all”). Validation errors from the server (HTTP{" "}
                <strong>422</strong>) show in a toast.
              </p>
            </div>
            <button type="button" className="btn-outline shrink-0 self-start" onClick={() => navigate(-1)}>
              Back
            </button>
          </div>

          <div className="freight-profiling-section freight-profiling-section--flush">
            <h4 className="freight-form-section-title">Vehicle capacity (tonnes)</h4>
            <p className="freight-profiling-hint max-w-3xl">
              Sent as root-level <code className="text-xs">vehicle_capacity_ids</code> on save (same CSV on every
              stored profiling row). Select every gross load band that applies to this user&apos;s fleet.
            </p>
            <div className="freight-prof-cap-grid">
              {caps.length === 0 && (
                <span className="freight-admin-muted mb-0 text-sm">No capacity bands returned from the server.</span>
              )}
              {caps.map((c) => {
                const active = vehicleIds.includes(Number(c.id));
                const minT = formatTonnes(c.min_load);
                const maxT = formatTonnes(c.max_load);
                const aria = `Capacity ${minT} to ${maxT} tonnes${active ? ", selected" : ""}`;
                return (
                  <button
                    key={c.id}
                    type="button"
                    aria-label={aria}
                    aria-pressed={active}
                    onClick={() =>
                      setVehicleIds((prev) => {
                        const cid = Number(c.id);
                        return active ? prev.filter((x) => x !== cid) : [...prev, cid];
                      })
                    }
                    className={`freight-prof-cap-btn ${active ? "freight-prof-cap-btn--active" : ""}`}
                  >
                    <span className="freight-prof-cap-btn__range">
                      {minT}–{maxT}
                    </span>
                    <span className="freight-prof-cap-btn__unit">tonnes</span>
                  </button>
                );
              })}
            </div>
            <p className="freight-prof-cap-summary">
              {vehicleIds.length} capacity band{vehicleIds.length === 1 ? "" : "s"} selected
            </p>
          </div>

          <div className="freight-profiling-section">
            <h4 className="freight-form-section-title mb-3">Locations</h4>
            {loadingCountLine}
            <div className="freight-profiling-two-col">
              <FreightProfilingLocationCombo
                title="Loading"
                subtitle="Where freight is picked up or loaded onto the vehicle."
                variant="loading"
                selections={loadingSelections}
                onChange={setLoadingSelections}
              />
              <FreightProfilingLocationCombo
                title="Unloading"
                subtitle="Where freight is delivered or unloaded."
                variant="unloading"
                selections={unloadingSelections}
                onChange={setUnloadingSelections}
              />
            </div>
          </div>

          <div className="freight-profiling-page-actions flex flex-wrap gap-3 border-t border-slate-100 pt-5">
            <button type="button" className="btn-primary min-h-[44px] px-5" disabled={saving} onClick={save}>
              {saving ? "Saving…" : "Save profiling"}
            </button>
            <button type="button" className="btn-outline min-h-[44px]" onClick={() => navigate(-1)}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightProfilingPage;
